import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus, FiSearch, FiGrid, FiDatabase, FiEye } from 'react-icons/fi';
import Portal from '../Portal';

const DataGridConfiguration = () => {
  const [dataGridConfigs, setDataGridConfigs] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDataGrid, setShowAddDataGrid] = useState(false);
  const [editingDataGrid, setEditingDataGrid] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dataGridResponse, endpointsResponse] = await Promise.all([
        adminAPI.getDataGridConfigs(),
        adminAPI.getEndpoints()
      ]);
      setDataGridConfigs(dataGridResponse.data.dataGridConfigs);
      setEndpoints(endpointsResponse.data.endpoints);
    } catch (error) {
      showMessage('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleDeleteDataGrid = async (dataGridId) => {
    if (!window.confirm('Are you sure you want to delete this Data Grid configuration? This action cannot be undone.')) {
      return;
    }

    try {
      await adminAPI.deleteDataGridConfig(dataGridId);
      setDataGridConfigs(dataGridConfigs.filter(dg => dg.id !== dataGridId));
      showMessage('Data Grid configuration deleted successfully', 'success');
    } catch (error) {
      showMessage(error.response?.data?.error || 'Failed to delete Data Grid configuration', 'error');
    }
  };

  const filteredDataGrids = dataGridConfigs.filter(dg =>
    dg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dg.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dg.endpointName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading Data Grid configurations...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Data Grid Configuration</h1>
        <button
          onClick={() => setShowAddDataGrid(true)}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <FiPlus style={{ marginRight: '5px' }} />
          Add Data Grid Config
        </button>
      </div>

      {message && (
        <div 
          className={messageType === 'error' ? 'error-message' : 'success-message'}
          style={{ marginBottom: '20px' }}
        >
          {message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="card-title">Data Grid Configurations ({filteredDataGrids.length})</h2>
            <div style={{ position: 'relative', width: '300px' }}>
              <FiSearch style={{ 
                position: 'absolute', 
                left: '10px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#666'
              }} />
              <input
                type="text"
                placeholder="Search data grid configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: '35px' }}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Endpoint</th>
                <th>Display Fields</th>
                <th>Default Size</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDataGrids.map(dg => (
                <tr key={dg.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiGrid style={{ marginRight: '8px', color: '#007bff' }} />
                      <strong>{dg.name}</strong>
                    </div>
                  </td>
                  <td>{dg.description || 'No description'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FiDatabase style={{ marginRight: '5px', color: '#6c757d' }} />
                      {dg.endpointName || `Endpoint ${dg.endpointId}`}
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info">
                      {dg.displayFields.length} fields
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getSizeBadgeClass(dg.defaultSize)}`}>
                      {dg.defaultSize.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span 
                      className={`badge ${dg.isActive ? 'badge-success' : 'badge-secondary'}`}
                    >
                      {dg.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => setEditingDataGrid(dg)}
                        className="btn btn-small btn-secondary"
                        title="Edit Data Grid Configuration"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        onClick={() => handleDeleteDataGrid(dg.id)}
                        className="btn btn-small btn-danger"
                        title="Delete Data Grid Configuration"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredDataGrids.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {searchTerm ? 'No Data Grid configurations found matching your search.' : 'No Data Grid configurations found.'}
              {!searchTerm && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={() => setShowAddDataGrid(true)}
                    className="btn btn-primary"
                  >
                    Create Your First Data Grid Configuration
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddDataGrid && (
        <Portal>
          <DataGridConfigModal
            endpoints={endpoints}
            onClose={() => setShowAddDataGrid(false)}
            onDataGridAdded={(newDataGrid) => {
              setDataGridConfigs([...dataGridConfigs, newDataGrid]);
              setShowAddDataGrid(false);
              showMessage('Data Grid configuration created successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}

      {editingDataGrid && (
        <Portal>
          <DataGridConfigModal
            dataGrid={editingDataGrid}
            endpoints={endpoints}
            onClose={() => setEditingDataGrid(null)}
            onDataGridUpdated={(updatedDataGrid) => {
              setDataGridConfigs(dataGridConfigs.map(dg => 
                dg.id === updatedDataGrid.id ? updatedDataGrid : dg
              ));
              setEditingDataGrid(null);
              showMessage('Data Grid configuration updated successfully', 'success');
            }}
            onError={(error) => showMessage(error, 'error')}
          />
        </Portal>
      )}
    </div>
  );
};

const getSizeBadgeClass = (size) => {
  switch (size) {
    case 'small': return 'badge-secondary';
    case 'medium': return 'badge-info';
    case 'large': return 'badge-primary';
    case 'xlarge': return 'badge-success';
    default: return 'badge-secondary';
  }
};

const DataGridConfigModal = ({ dataGrid, endpoints, onClose, onDataGridAdded, onDataGridUpdated, onError }) => {
  const [formData, setFormData] = useState({
    name: dataGrid?.name || '',
    description: dataGrid?.description || '',
    endpointId: dataGrid?.endpointId || '',
    requestBody: dataGrid?.requestBody ? JSON.stringify(dataGrid.requestBody, null, 2) : '',
    displayFields: dataGrid?.displayFields || [],
    defaultSize: dataGrid?.defaultSize || 'large',
    refreshInterval: dataGrid?.refreshInterval || 300
  });
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);

  const sizes = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'xlarge', label: 'Extra Large' }
  ];

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewData(null);
    setPreviewError(null);
    setAvailableFields([]);

    try {
      if (!formData.endpointId) {
        throw new Error('Please select an endpoint first');
      }

      const selectedEndpoint = endpoints.find(ep => ep.id.toString() === formData.endpointId.toString());
      if (!selectedEndpoint) {
        throw new Error('Selected endpoint not found');
      }

      // Create test endpoint with custom request body if provided
      const testEndpoint = {
        ...selectedEndpoint,
        body: formData.requestBody ? JSON.parse(formData.requestBody) : selectedEndpoint.body
      };

      const response = await adminAPI.testEndpoint(testEndpoint);
      
      if (response.data.success) {
        const apiData = response.data.data;
        setPreviewData(apiData);
        
        // Extract available fields from the response
        if (Array.isArray(apiData)) {
          if (apiData.length > 0) {
            setAvailableFields(Object.keys(apiData[0]));
          }
        } else if (apiData.result && Array.isArray(apiData.result)) {
          if (apiData.result.length > 0) {
            setAvailableFields(Object.keys(apiData.result[0]));
          }
        } else if (typeof apiData === 'object') {
          setAvailableFields(Object.keys(apiData));
        }
      } else {
        throw new Error(response.data.error || 'Failed to get data from endpoint');
      }
    } catch (error) {
      setPreviewError(error.response?.data?.error || error.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleFieldToggle = (fieldName) => {
    const newFields = formData.displayFields.includes(fieldName)
      ? formData.displayFields.filter(f => f !== fieldName)
      : [...formData.displayFields, fieldName];
    
    setFormData({ ...formData, displayFields: newFields });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let requestBody = null;

      if (formData.requestBody.trim()) {
        try {
          requestBody = JSON.parse(formData.requestBody);
        } catch (error) {
          throw new Error('Invalid JSON in request body field');
        }
      }

      if (formData.displayFields.length === 0) {
        throw new Error('Please select at least one field to display');
      }

      const payload = {
        ...formData,
        requestBody,
        refreshInterval: parseInt(formData.refreshInterval)
      };

      if (dataGrid) {
        const response = await adminAPI.updateDataGridConfig(dataGrid.id, payload);
        onDataGridUpdated(response.data.dataGridConfig || { ...dataGrid, ...payload });
      } else {
        const response = await adminAPI.createDataGridConfig(payload);
        onDataGridAdded(response.data.dataGridConfig || { id: Date.now(), ...payload });
      }
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to save Data Grid configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h3>{dataGrid ? 'Edit Data Grid Configuration' : 'Add New Data Grid Configuration'}</h3>
          <button onClick={onClose} className="btn btn-small btn-secondary">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="form-input"
              required
              placeholder="e.g., Sales Data Table"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="form-input"
              rows="2"
              placeholder="Brief description of this data grid"
            />
          </div>

          <div className="form-group">
            <label className="form-label">API Endpoint *</label>
            <select
              value={formData.endpointId}
              onChange={(e) => setFormData({...formData, endpointId: e.target.value})}
              className="form-input"
              required
            >
              <option value="">Select an endpoint</option>
              {endpoints.map(endpoint => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.name} ({endpoint.method} {endpoint.url})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Custom Request Body (JSON)</label>
            <textarea
              value={formData.requestBody}
              onChange={(e) => setFormData({...formData, requestBody: e.target.value})}
              className="form-input"
              rows="4"
              placeholder='{"fromdate": "2025-08-01", "todate": "2025-08-15"}'
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Optional: Override the endpoint's default request body
            </small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Default Size *</label>
              <select
                value={formData.defaultSize}
                onChange={(e) => setFormData({...formData, defaultSize: e.target.value})}
                className="form-input"
                required
              >
                {sizes.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Refresh Interval (seconds)</label>
              <input
                type="number"
                value={formData.refreshInterval}
                onChange={(e) => setFormData({...formData, refreshInterval: e.target.value})}
                className="form-input"
                min="30"
                placeholder="300"
              />
            </div>
          </div>

          {/* Preview Section */}
          <div className="form-group" style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, color: '#333' }}>Preview & Field Selection</h4>
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewing || !formData.endpointId}
                className="btn btn-info"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <FiEye style={{ marginRight: '5px' }} />
                {previewing ? 'Loading Preview...' : 'Preview Data'}
              </button>
            </div>

            {availableFields.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">Select Fields to Display *</label>
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '5px', 
                  padding: '10px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  {availableFields.map(field => (
                    <label key={field} style={{ 
                      display: 'block', 
                      marginBottom: '5px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.displayFields.includes(field)}
                        onChange={() => handleFieldToggle(field)}
                        style={{ marginRight: '8px' }}
                      />
                      {field}
                    </label>
                  ))}
                </div>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Select which fields from the API response to display in the data grid
                </small>
              </div>
            )}

            {previewData && (
              <div style={{ 
                backgroundColor: '#d4edda', 
                border: '1px solid #c3e6cb', 
                borderRadius: '5px', 
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#155724', margin: '0 0 10px 0' }}>✅ Preview Data</h5>
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '3px', 
                  padding: '10px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    color: '#495057',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {JSON.stringify(previewData, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {previewError && (
              <div style={{ 
                backgroundColor: '#f8d7da', 
                border: '1px solid #f5c6cb', 
                borderRadius: '5px', 
                padding: '15px',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#721c24', margin: '0 0 10px 0' }}>❌ Preview Failed</h5>
                <p style={{ color: '#721c24', margin: 0 }}>{previewError}</p>
              </div>
            )}

            {!previewData && !previewError && !previewing && (
              <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                Preview your endpoint data to select which fields to display in the data grid.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : (dataGrid ? 'Update Data Grid' : 'Create Data Grid')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DataGridConfiguration;
